function initOMBundle(root = document) {
  root.querySelectorAll('[data-om-bundle]:not([data-om-bundle-ready])').forEach((bundle) => {
    bundle.setAttribute('data-om-bundle-ready', 'true');

    const track = bundle.querySelector('[data-om-bundle-track]');
    const countEl = bundle.querySelector('[data-om-bundle-count]');
    const totalEl = bundle.querySelector('[data-om-bundle-total]');
    const totalFinalEl = totalEl?.querySelector('[data-om-bundle-total-final]');
    const totalCompareEl = totalEl?.querySelector('[data-om-bundle-total-compare]');
    const totalSavingEl = totalEl?.querySelector('[data-om-bundle-total-saving]') || totalEl?.querySelector('strong');
    const prev = bundle.querySelector('[data-om-bundle-prev]');
    const next = bundle.querySelector('[data-om-bundle-next]');

    const formatMoney = (cents) => {
      const rounded = Math.round(Number(cents) || 0);
      try {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(rounded, bundle.dataset.moneyFormat || undefined);
      } catch (_) {}
      return `${Math.round(rounded / 100).toLocaleString('cs-CZ')} Kč`;
    };

    const parsePercent = (value, fallback = 0) => {
      if (value === undefined || value === null || value === '') return fallback;
      const parsed = Number(String(value).replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const discountSource = () => String(bundle.dataset.discountSource || 'tier_percent').trim();

    const getDiscountPercent = (totalProducts) => {
      const source = discountSource();

      if (source === 'none' || source === 'compare_at_price') return 0;

      if (source === 'manual_percent') {
        return parsePercent(bundle.getAttribute('data-discount-percent') || bundle.dataset.discountPercent, 0);
      }

      // Default and backward-compatible mode: tiered percentage discount.
      // Supports current value "tier_percent" and older/alternate values in case Shopify kept old saved settings.
      const clamped = Math.max(2, Math.min(10, Number(totalProducts) || 2));
      const tierValue = bundle.getAttribute(`data-discount-${clamped}`) || bundle.dataset[`discount${clamped}`];
      const fallbackValue = bundle.getAttribute('data-discount-percent') || bundle.dataset.discountPercent;
      return parsePercent(tierValue, parsePercent(fallbackValue, 0));
    };

    const selectedInputs = () => Array.from(bundle.querySelectorAll('input[name="om_bundle_product"]:checked:not(:disabled)'));
    const allCards = () => Array.from(bundle.querySelectorAll('[data-om-bundle-item]'));

    const getCardInput = (card) => card.querySelector('input[name="om_bundle_product"]');

    const getVisibleVariantOptions = (card) => {
      const selects = Array.from(card.querySelectorAll('[data-om-bundle-option]'));
      return selects.map((select) => ({
        position: Number(select.dataset.optionPosition || 1),
        value: select.value,
      }));
    };

    const parseVariants = (card) => {
      if (card.__omBundleVariants) return card.__omBundleVariants;
      const script = card.querySelector('[data-om-bundle-variants]');
      if (!script?.textContent) return [];
      try {
        card.__omBundleVariants = JSON.parse(script.textContent);
        return card.__omBundleVariants;
      } catch (error) {
        console.warn('OM bundle variants JSON parse failed', error);
        card.__omBundleVariants = [];
        return [];
      }
    };

    const variantMatchesSelectedOptions = (variant, selectedOptions) => {
      if (!selectedOptions.length) return true;
      const options = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return selectedOptions.every(({ position, value }) => String(options[position - 1] || '') === String(value));
    };

    const updateCardVariant = (card) => {
      const input = getCardInput(card);
      if (!input) return;

      const variants = parseVariants(card);
      const selectedOptions = getVisibleVariantOptions(card);
      let variant = variants.find((candidate) => candidate.available !== false && variantMatchesSelectedOptions(candidate, selectedOptions));
      if (!variant) variant = variants.find((candidate) => variantMatchesSelectedOptions(candidate, selectedOptions));
      if (!variant) variant = variants.find((candidate) => candidate.available !== false) || variants[0];
      if (!variant) return;

      input.value = String(variant.id);
      input.dataset.variantId = String(variant.id);
      input.dataset.price = String(variant.price || 0);
      input.dataset.compareAt = String(variant.compare_at_price || 0);
      input.disabled = variant.available === false;
      card.classList.toggle('is-disabled', input.disabled);
      if (input.disabled && input.checked) input.checked = false;
    };

    const calculateDiscount = (input, totalProductsForDiscount) => {
      const basePrice = Number(input.dataset.price) || 0;
      const compareAt = Number(input.dataset.compareAt) || 0;

      if (discountSource() === 'compare_at_price') {
        const savings = compareAt > basePrice ? compareAt - basePrice : 0;
        return {
          basePrice,
          comparePrice: savings > 0 ? compareAt : 0,
          finalPrice: basePrice,
          savings,
        };
      }

      const percent = getDiscountPercent(totalProductsForDiscount);
      const savings = basePrice * percent / 100;
      return {
        basePrice,
        comparePrice: savings > 0 ? basePrice : 0,
        finalPrice: Math.max(0, basePrice - savings),
        savings,
      };
    };

    const updateCardPricing = (card, totalProductsForDiscount) => {
      const input = getCardInput(card);
      if (!input) return;

      const { comparePrice, finalPrice, savings } = calculateDiscount(input, totalProductsForDiscount);
      input.dataset.savings = String(Math.round(savings));
      card.dataset.savings = String(Math.round(savings));

      const compareEl = card.querySelector('[data-om-bundle-price-compare]');
      const finalEl = card.querySelector('[data-om-bundle-price-final]');
      const savingEl = card.querySelector('[data-om-bundle-saving]');

      if (compareEl) {
        if (comparePrice > 0 && Math.round(comparePrice) !== Math.round(finalPrice)) {
          compareEl.textContent = formatMoney(comparePrice);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      if (finalEl) finalEl.textContent = formatMoney(finalPrice);

      if (savingEl) {
        const showSavings = bundle.getAttribute('data-show-savings-per-item') !== 'false';
        if (savings > 0 && showSavings) {
          savingEl.textContent = `Ušetříte ${formatMoney(savings)}`;
          savingEl.hidden = false;
        } else {
          savingEl.textContent = '';
          savingEl.hidden = true;
        }
      }
    };

    const updateArrowState = () => {
      if (!track) return;
      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth - 2);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScrollLeft;
    };

    const updateSummary = () => {
      allCards().forEach(updateCardVariant);

      const selected = selectedInputs();
      const count = selected.length;
      const selectedTotalProducts = 1 + count;

      allCards().forEach((card) => {
        const input = getCardInput(card);
        if (!input) return;
        card.classList.toggle('is-selected', input.checked);
        card.setAttribute('data-selected', input.checked ? 'true' : 'false');
        // Show each unselected item as a preview of what happens if the customer adds it.
        // Selected items use the current tier. This makes the per-item saving visible immediately.
        const previewTotalProducts = input.checked ? selectedTotalProducts : Math.max(2, selectedTotalProducts + 1);
        updateCardPricing(card, previewTotalProducts);
      });

      if (countEl) countEl.textContent = `Vybráno: ${count} položek`;

      const totalProducts = 1 + count;
      const selectedDiscounts = selected.map((input) => calculateDiscount(input, totalProducts));
      const totalBasePrice = selectedDiscounts.reduce((sum, item) => sum + item.basePrice, 0);
      const totalFinalPrice = selectedDiscounts.reduce((sum, item) => sum + item.finalPrice, 0);
      const totalSavings = selectedDiscounts.reduce((sum, item) => sum + item.savings, 0);
      if (totalEl) {
        const showTotalSavings = bundle.getAttribute('data-show-total-savings') !== 'false';
        if (totalSavings > 0 && showTotalSavings) {
          if (totalCompareEl) totalCompareEl.textContent = formatMoney(totalBasePrice);
          if (totalFinalEl) totalFinalEl.textContent = formatMoney(totalFinalPrice);
          if (totalSavingEl) totalSavingEl.textContent = formatMoney(totalSavings);
          totalEl.hidden = false;
          totalEl.classList.remove('is-updated');
          // Restart a very light pulse without changing functionality.
          requestAnimationFrame(() => totalEl.classList.add('is-updated'));
        } else {
          if (totalCompareEl) totalCompareEl.textContent = '';
          if (totalFinalEl) totalFinalEl.textContent = formatMoney(0);
          if (totalSavingEl) totalSavingEl.textContent = formatMoney(0);
          totalEl.hidden = true;
          totalEl.classList.remove('is-updated');
        }
      }

      updateArrowState();
    };

    const setCardSelected = (input, checked) => {
      input.checked = checked;
      const card = input.closest('[data-om-bundle-item]');
      card?.classList.toggle('is-selected', input.checked);
      card?.setAttribute('data-selected', input.checked ? 'true' : 'false');
      updateSummary();
      bundle.dispatchEvent(new CustomEvent('om:bundle-change', { bubbles: true }));
    };

    bundle.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('select, option, input, label, button, a')) return;
      const card = target.closest('[data-om-bundle-item]');
      if (!card) return;
      const input = getCardInput(card);
      if (!input || input.disabled) return;
      setCardSelected(input, !input.checked);
    });

    bundle.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const card = event.target instanceof Element ? event.target.closest('[data-om-bundle-item]') : null;
      if (!card || event.target.closest('select, input, button, a')) return;
      const input = getCardInput(card);
      if (!input || input.disabled) return;
      event.preventDefault();
      setCardSelected(input, !input.checked);
    });

    bundle.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('input[name="om_bundle_product"]')) {
        const input = target;
        const card = input.closest('[data-om-bundle-item]');
        card?.classList.toggle('is-selected', input.checked);
        card?.setAttribute('data-selected', input.checked ? 'true' : 'false');
        updateSummary();
        bundle.dispatchEvent(new CustomEvent('om:bundle-change', { bubbles: true }));
        return;
      }

      if (target.matches('[data-om-bundle-option]')) {
        const card = target.closest('[data-om-bundle-item]');
        if (card) {
          updateCardVariant(card);
          updateSummary();
          bundle.dispatchEvent(new CustomEvent('om:bundle-change', { bubbles: true }));
        }
      }
    });

    const scrollByCard = (direction) => {
      if (!track) return;
      const first = track.querySelector('[data-om-bundle-slide]');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '12') || 12;
      const amount = first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.85;
      track.scrollBy({ left: direction * amount, behavior: 'smooth' });
      window.setTimeout(updateArrowState, 260);
    };

    prev?.addEventListener('click', () => scrollByCard(-1));
    next?.addEventListener('click', () => scrollByCard(1));
    track?.addEventListener('scroll', updateArrowState, { passive: true });
    window.addEventListener('resize', updateArrowState, { passive: true });

    updateSummary();
    window.setTimeout(updateArrowState, 50);
  });
}

initOMBundle();
document.addEventListener('shopify:section:load', (event) => initOMBundle(event.target));
